import React, { useContext, useState, useEffect } from 'react';
import _ from 'lodash'; // eslint-disable-line

// Nucleus Components
import { extend } from '@thd-nucleus/data-sources';
import {
  RentalEstimate,
  RentalEstimateWrapperProvider
} from '@thd-olt-component-react/rental-estimate';

// Local Components
import { Alert, Button } from '@thd-olt-component-react/core-ui';
import { ReservationUpdateApiContext } from '../../../context/ReservationUpdateApiContext';
import { ReservationContext } from '../../../context/reservation-context';
import { ReservationLookupApiContext } from '../../../context/ReservationLookupApiContext';
import { FeatureSwitchContext } from '../../../context/feature-switch-context';
import { FormSection } from '../../../components/form-section/form-section';
import { convertTimestampForEstimate } from '../../../utils/mapper';
import { CurrentTimes } from '../../../components/current-times/current-times';
import helper from '../../../utils/helper';
import { StoreDetails } from '../../../components/store-details/store-details';
import { StoreHours } from '../../../components/store-hours/store-hours';

// CSS
import './summary.scss';

export const Summary = () => {

  const [reservationState, dispatch] = useContext(ReservationContext);
  const { data: reservationApiData } = useContext(ReservationLookupApiContext);
  const { data: updateApiData, error, executeRequest } = useContext(ReservationUpdateApiContext);
  const { estimateGraphQlEnabled } = useContext(FeatureSwitchContext);

  const { reservationModifyStore, selectedAvailability } = reservationState;

  useEffect(() => {
    if (updateApiData || error) {
      helper.scrollToSection('SummaryHeader');
    }
  }, [updateApiData, error]);

  return (
    <div className="modify-summary">
      {!_.isEmpty(error) && <Alert title="Error:" type="danger" message="Please try again" noClose />}
      {!_.isEmpty(updateApiData) && <Alert title="Success:" type="success" message="Reservation modified" />}
      <FormSection identifier="SummaryHeader" display title="Reservation Summary">
        <div className="summary-header-container">
          <span className="summary-header-text">Does this look right?</span>
          <Button inline link onClick={() => dispatch({ type: 'SET_EDIT_STORE' })}>Make
            Changes
          </Button>
        </div>
      </FormSection>
      <FormSection identifier="Location" display title="Location">
        <>
          {!_.isEmpty(reservationModifyStore) && (
            <div className="summary-store-container" data-testid="summary-store-container">
              <div className="summary-store">
                <StoreDetails store={reservationModifyStore} />
              </div>
              <div className="summary-store-hours">
                <StoreHours store={reservationModifyStore} padding="small" />
              </div>
            </div>
          )}
        </>
      </FormSection>
      <FormSection identifier="DaysAndTimes" display title="New Days and Times">
        <div className="summary-store-times" data-testid="summary-store-times">
          <CurrentTimes compare />
        </div>
      </FormSection>
      <FormSection identifier="NewRentalEstimate" display title="New Rental Estimate">
        <>
          {!_.isEmpty(reservationApiData) && (
            <div className="estimate-container">
              <RentalEstimateWrapperProvider
                pickupTime={selectedAvailability?.start}
                returnTime={selectedAvailability?.end}
                storeId={reservationApiData?.store_number}
                rentalCategory={_.first(reservationApiData?.tools)?.category_code}
                rentalSubCategory={_.first(reservationApiData?.tools)?.subcategory_code}
                damageProtection={reservationApiData?.damage_protection || false}
                graphQlEnabled={estimateGraphQlEnabled}
                hideDeposit // TODO: make this lookup login and SVOC_ID if possible
              >
                <RentalEstimate />
              </RentalEstimateWrapperProvider>
            </div>
          )}
        </>
      </FormSection>
      <FormSection
        identifier="OldRentalEstimate"
        display
        accordion
        title="Original Rental Estimate"
      >
        <>
          {!_.isEmpty(reservationApiData) && (
            <div className="estimate-container">
              <RentalEstimateWrapperProvider
                pickupTime={convertTimestampForEstimate(reservationApiData?.pickup_time)}
                returnTime={convertTimestampForEstimate(reservationApiData?.return_time)}
                storeId={reservationApiData?.store_number}
                rentalCategory={_.first(reservationApiData?.tools)?.category_code}
                rentalSubCategory={_.first(reservationApiData?.tools)?.subcategory_code}
                damageProtection={reservationApiData?.damage_protection || false}
                graphQlEnabled={estimateGraphQlEnabled}
                hideDeposit // TODO: make this lookup login and SVOC_ID if possible
              >
                <RentalEstimate />
              </RentalEstimateWrapperProvider>
            </div>
          )}
        </>
      </FormSection>
      <FormSection identifier="ModifyButtons" display title="">
        <>
          <Button onClick={executeRequest}>Update Reservation</Button>
          <Button onClick={() => window.location.reload()} link>Exit</Button>
        </>
      </FormSection>
    </div>
  );
};

Summary.propTypes = {};

Summary.dataModel = extend(RentalEstimateWrapperProvider);
